import { HttpErrorResponse } from '@angular/common/http';
import { Component, Inject, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { Subject } from 'rxjs';
import { CreateTilvalgRequest } from 'src/app/api/models/OK/Egis/Service/Models/Request/create-tilvalg-request';
import { TilvalgDto } from 'src/app/api/models/OK/Egis/Service/Models/DTO/tilvalg-dto';
import { TilvalgGroupDto } from 'src/app/api/models/OK/Egis/Service/Models/DTO/tilvalg-group-dto';
import { UpdateTilvalgRequest } from 'src/app/api/models/OK/Egis/Service/Models/Request/update-tilvalg-request';
import { TilvalgService, VareService } from 'src/app/api/services';
import { TilvalgEditDialogData } from 'src/app/components/aftale/models';
import { EGIS_SERVICE_CALLER } from 'src/app/constants';
import { ErrorHandlerService } from 'src/app/services/error-handler.service';
import { LoaderService } from 'src/app/services/loader.service';
import { finalize, takeUntil } from 'rxjs/operators';
import { DoesVarenrExistsResponseDto } from 'src/app/api/models/OK/Egis/Service/G4Communication/DTO/does-varenr-exists-response-dto';

@Component({
    selector: 'app-tilvalg-edit-dialog',
    templateUrl: './tilvalg-edit-dialog.component.html',
    styleUrls: ['./tilvalg-edit-dialog.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class TilvalgEditDialogComponent implements OnInit, OnDestroy {
  tilvalg: TilvalgDto;
  tilvalgGroups: TilvalgGroupDto[];
  form: UntypedFormGroup;
  vareInfoCollected: boolean = false;
  noVareInfoInG4: boolean = false;
  unsubscribe$ = new Subject<void>();

  get name() {
    return this.form.get('name');
  }

  get okKundeVarenr() {
    return this.form.get('okKundeVarenr');
  }

  get tilvalgType() {
    return this.form.get('tilvalgType');
  }

  get isActive() {
    return this.form.get('isActive');
  }

  constructor(
    private service: TilvalgService,
    private vareService: VareService,
    private loader: LoaderService,
    private errorHandler: ErrorHandlerService,
    private formBuilder: UntypedFormBuilder,
    public dialogRef: MatDialogRef<TilvalgEditDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: TilvalgEditDialogData
  ) {
    this.tilvalg = data.tilvalg;
    this.tilvalgGroups = data.tilvalgGroups;
    this.form = this.formBuilder.group({
      name: [this.tilvalg?.name, [Validators.required, Validators.minLength(3)]],
      okKundeVarenr: [this.tilvalg?.okKundeVarenr, [Validators.required, Validators.minLength(3)]],
      tilvalgType: [this.tilvalg?.tilvalgType, [Validators.required]],
      isActive: [this.tilvalg?.isActive],
    });
  }

  ngOnInit(): void {}

  ngOnDestroy(): void {}

  click() {
    if (this.tilvalg) {
      this.update();
    } else {
      this.add();
    }
  }

  add() {
    if (!this.form.valid) return;
    const data: CreateTilvalgRequest = {
      name: this.name.value,
      okKundeVarenr: Number(this.okKundeVarenr.value),
      tilvalgType: this.tilvalgType.value,
      isActive: this.isActive.value ? this.isActive.value : true,
    };

    this.loader.show();
    this.service
      .createTilvalg({
        caller: EGIS_SERVICE_CALLER,
        body: data,
      })
      .pipe(
        finalize(() => {
          this.loader.hide();
        })
      )
      .subscribe(
        (result) => {
          this.dialogRef.close(result);
        },
        (error) => {
          this.errorHandler.HandleError(error);
        }
      );
  }

  update() {
    if (!this.form.valid) return;
    const data: UpdateTilvalgRequest = {
      name: this.name.value,
      tilvalgId: this.tilvalg.id,
      okKundeVarenr: Number(this.okKundeVarenr.value),
      tilvalgType: this.tilvalgType.value,
      isActive: this.isActive.value ? this.isActive.value : true,
    };

    this.loader.show();
    this.service
      .updateTilvalg({
        caller: EGIS_SERVICE_CALLER,
        body: data,
        tilvalgId: this.tilvalg.id,
      })
      .pipe(
        finalize(() => {
          this.loader.hide();
        })
      )
      .subscribe(
        (result) => {
          this.dialogRef.close(result);
        },
        (error) => {
          this.errorHandler.HandleError(error);
        }
      );
  }

  delete() {
    this.loader.show();
    this.service
      .deleteTilvalg({
        caller: EGIS_SERVICE_CALLER,
        tilvalgId: this.tilvalg.id,
      })
      .pipe(
        finalize(() => {
          this.loader.hide();
        })
      )
      .subscribe(
        (result) => {
          this.dialogRef.close(result);
        },
        (error) => {
          this.errorHandler.HandleError(error);
        }
      );
  }

  getInfoForVareNr(vareNr: number) {
    this.vareService
      .doesVareNrExist({
        caller: EGIS_SERVICE_CALLER,
        vareNr: this.okKundeVarenr.value,
      })

      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(
        (vareInfo: DoesVarenrExistsResponseDto) => {
          this.noVareInfoInG4 = !(vareInfo && vareInfo.doesVarenrExists);
          this.vareInfoCollected = !this.noVareInfoInG4;
          if (this.vareInfoCollected) {
            this.okKundeVarenr.disable();
          }
        },
        (error: HttpErrorResponse) => {
          this.vareInfoCollected = false;
          this.errorHandler.HandleError(error);
        }
      );
  }
}
